
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
  ResponsiveContainer,
  Tooltip,
  Cell,
} from "recharts";
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon } from "@chakra-ui/icons";
import { fetchImportData } from "../../actions/sector/sectorActions";
import {
  Box,
  Flex,
  IconButton,
  Text,
  Input,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";

const ImportData = ({ sectorName, sectorWiseCategories, categories = [] }) => {
  const dispatch = useDispatch();
  const [activeIndex, setActiveIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [hoveredBarIndex, setHoveredBarIndex] = useState(null);

  const { loading, data: importData, error } = useSelector(
    (state) =>
      state.importDataReducer || {
        loading: false,
        data: null,
        error: null,
      }
  );

  useEffect(() => {
    if (!importData) {
      dispatch(fetchImportData());
    }
  }, [dispatch, importData]);

  const displayData = importData?.data || importData;
  const [activeCategories, setActiveCategories] = useState([]);

  useEffect(() => {
    if (categories?.length) {
      setActiveCategories(categories);
    } else if (displayData) {
      setActiveCategories(Object.keys(displayData).filter((k) => k !== "_summary"));
    }
  }, [categories, displayData, sectorName, sectorWiseCategories]);

  const selectedSectorCategories = useMemo(() => {
    if (!displayData || !activeCategories.length) return [];

    const displayKeys = Object.keys(displayData).filter(
      (cat) => cat !== "_summary"
    );

    return activeCategories
      .map((cat) => {
        const matchedKey = displayKeys.find(
          (k) => k.toLowerCase().trim() === cat.toLowerCase().trim()
        );
        if (!matchedKey) return null;

        const categoryData = displayData[matchedKey];
        const countryMap = {};

        Object.entries(categoryData || {}).forEach(([commodity, data]) => {
          if (commodity === "_summary") return;
          Object.entries(data || {}).forEach(([country, values]) => {
            if (country === "_summary") return;
            const val = parseFloat(values?.value_usd_million || 0);
            countryMap[country] = (countryMap[country] || 0) + val;
          });
        });

        const sorted = Object.entries(countryMap)
          .map(([name, value]) => ({
            name,
            value: parseFloat(value.toFixed(2)),
          }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 7);

        const max = Math.max(...sorted.map((d) => d.value), 1);
        const min = Math.min(...sorted.map((d) => d.value));
        const colors = ["#30D5C8", "#FFAA00"]; // teal & orange

        const chartData = sorted.map((item, i) => ({
          ...item,
          normalizedValue:
            max === min
              ? 200
              : 30 +
              ((Math.log10(max + 1) - Math.log10(item.value + 1)) /
                (Math.log10(max + 1) - Math.log10(min + 1))) *
              170,
          fill: colors[i % colors.length],
        }));

        return { category: matchedKey, chartData };
      })
      .filter(Boolean);
  }, [displayData, activeCategories]);

  useEffect(() => {
    if (
      selectedSectorCategories.length > 0 &&
      (
        !activeCategory ||
        !selectedSectorCategories.some(({ category }) => category === activeCategory)
      )
    ) {
      setActiveCategory(selectedSectorCategories[0].category.trim());
    }
  }, [selectedSectorCategories, activeCategory]);

  // Arrow navigation
  const handlePrev = () => {
    setActiveIndex((prev) =>
      prev === 0 ? selectedSectorCategories.length - 1 : prev - 1
    );
    setActiveCategory(selectedSectorCategories[activeIndex]?.category);
  };

  const handleNext = () => {
    setActiveIndex((prev) =>
      prev === selectedSectorCategories.length - 1 ? 0 : prev + 1
    );
    setActiveCategory(selectedSectorCategories[activeIndex]?.category);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;
  if (!displayData || !activeCategories.length) {
    return <div>No data available for the selected sector.</div>;
  }

  const activeCategoryData =
    selectedSectorCategories.find((item) => item.category === activeCategory) || {};

  const { category, chartData = [] } = activeCategoryData;


  const filteredLegend = chartData?.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box
      mt={6}
      bg="#F2F7F7"
      border="1px solid rgba(0,0,0,.06)"
      w="670px"
      borderRadius={14}
    >
      <Flex align="flex-start" gap={4} pt={5} pl={2} pb={4}>
        <Box flex="1">
          <Flex align="center" mb={3} gap={4}>
            <Text fontSize="lg" fontWeight="bold">
              Import Demand Of India
            </Text>

            <Box w="260px">
              <InputGroup>
                <Input
                  type="text"
                  placeholder="Search Product"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  fontSize="13px"
                  borderRadius="6px"
                  bg={"white"}
                  px={3}
                  py={2}
                />
                <InputRightElement>
                  <SearchIcon color="#30D5C8" boxSize={4} />
                </InputRightElement>
              </InputGroup>
            </Box>
          </Flex>

          {/* Row 2: Chart */}
          <Box
            position="relative"
            bg="white"
            border="1px solid rgba(0,0,0,.06)"
            borderRadius="14px"
            boxShadow="sm"
            px={4}
            py={6}
            w="500px"
            h="100%"
          >
            {/* Left/Right arrows */}
            <IconButton
              aria-label="Previous"
              icon={<ChevronLeftIcon boxSize={8} />}
              onClick={handlePrev}
              position="absolute"
              top="75%"
              left="6px"
              transform="translateY(-50%)"
              borderRadius="full"
              bg="white"
              color="#30D5C8"
              boxShadow="md"
              size="sm"
              _hover={{
                color: "white",
                bg: "#30D5C8",
              }}
            />
            <IconButton
              aria-label="Next"
              icon={<ChevronRightIcon boxSize={8} />}
              onClick={handleNext}
              position="absolute"
              top="75%"
              right="6px"
              transform="translateY(-50%)"
              borderRadius="full"
              bg="white"
              color="#30D5C8"
              boxShadow="md"
              size="sm"
              _hover={{
                color: "white",
                bg: "#30D5C8",
              }}
            />

            <Flex gap={8} align="flex-start" justify="center">
              {/* Chart */}
              <Box flex="0 0 auto" w="250px" h="250px">
                <ResponsiveContainer width="100%" height="100%">
                  <RadialBarChart
                    cx="50%"
                    cy="50%"
                    innerRadius={10}
                    outerRadius={100}
                    barSize={10}
                    data={chartData}
                    startAngle={90}
                    endAngle={-250}
                  >
                    <PolarAngleAxis
                      type="number"
                      domain={[0, 200]}
                      angleAxisId={0}
                      tick={{ fill: "#666", fontSize: 10 }}
                      tickLine={false}
                      ticks={[0, 50, 100, 150, 200]}
                      axisLine={{ stroke: "#E6E6E6", strokeWidth: 1 }}
                    />

                    <Tooltip
                      content={({ payload }) => {
                        if (!payload || !payload.length) return null;
                        const item = payload[0].payload;
                        return (
                          <Box
                            bg="white"
                            border="1px solid #ccc"
                            p={2}
                            borderRadius="md"
                            fontSize="xs"
                          >
                            <strong>{item.name}</strong>
                            <div>Value: ${item.value.toLocaleString()}M</div>
                          </Box>
                        );
                      }}
                    />

                    <RadialBar
                      dataKey="normalizedValue"
                      cornerRadius={0}
                      clockWise
                      barSize={10}
                      isAnimationActive
                      background
                      onMouseEnter={(data, index) => setHoveredBarIndex(index)}
                      onMouseLeave={() => setHoveredBarIndex(null)}
                      style={{ cursor: "pointer" }}
                    >
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.fill}
                          fillOpacity={
                            hoveredBarIndex === null
                              ? 1
                              : hoveredBarIndex === index
                                ? 1
                                : 0.2
                          }
                        />
                      ))}
                    </RadialBar>

                    <text
                      x="50%"
                      y="50%"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill="#666"
                      fontSize={12}
                    >
                      Top Importers
                    </text>
                  </RadialBarChart>
                </ResponsiveContainer>
              </Box>

              {/* Legend inside chart box */}
              <Box
                minW="180px"
                bg="#F2F7F7"
                border="1px solid rgba(0,0,0,.06)"
                pl={4}
                borderRadius={7}
              >
                <Text fontSize="md" fontWeight="bold" mb={2}>
                  LEGEND
                </Text>
                {filteredLegend?.map((item, idx) => (
                  <Flex key={idx} align="center" mb={2}>
                    <Box
                      w="10px"
                      h="10px"
                      borderRadius="full"
                      bg={item.fill}
                      mr={2}
                    />
                    <Text fontSize="sm" color="gray.700">
                      {item.name}
                    </Text>
                  </Flex>
                ))}
              </Box>
            </Flex>
            <Text fontSize="sm" color="gray.600">
              Total import potential for India according to value (US $ MM)
            </Text>
          </Box>
        </Box>

        {/* Right side */}
        <Box
          maxW="190px"
          bg="#F2F7F7"
          border="1px solid rgba(0,0,0,.06)"
          borderRadius="5px"
          boxShadow="sm"
          px={4}
          py={3}
          w="200px"
          maxH="370px"
          overflowY="auto"
          sx={{
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": {
              background: "#ccc",
              borderRadius: "3px",
            },
          }}
        >
          {selectedSectorCategories && selectedSectorCategories.length > 0 ? (
            Array.from(
              new Map(
                selectedSectorCategories.map(({ category }) => [
                  category.toLowerCase().trim(),
                  category.trim(),
                ])
              ).values()
            ).map((cat) => (
              <Box
                key={cat}
                onClick={() => setActiveCategory(cat)}
                cursor="pointer"
                px={2}
                py={1}
                mb={2}
                borderRadius="md"
                bg={activeCategory === cat ? "#30D5C8" : "white"}
                color={activeCategory === cat ? "white" : "gray.800"}
                fontSize="sm"
                fontWeight={activeCategory === cat ? "600" : "400"}
                _hover={{ bg: activeCategory === cat ? "#25b9ad" : "gray.100" }}
              >
                {cat}
              </Box>
            ))
          ) : (
            <Text fontSize="sm" color="gray.500">
              Not available for this sector
            </Text>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

export default ImportData;
